import { Component, forwardRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core'
import { ControlValueAccessor, FormArray, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms'
import { Subscription } from 'rxjs'
import { coerceBooleanProperty } from '@angular/cdk/coercion'

import { FileInputComponent } from '@shared/components/form/file-upload/file-input.component'

@Component({
  selector: 'lin-form-file-picker',
  template: `
    <ng-content></ng-content>
    @for (fileControl of fileControls.controls; track $index; let i = $index) {
      @if (fileControl.value) {
        <lin-form-file-row name="{{ fileControl.value.name }}" (delete)="fileControls.removeAt(i)"></lin-form-file-row>
      }
      <lin-form-file-input [formControl]="fileControl"></lin-form-file-input>
    }

    @if (uploadsPossible && !isDisabled) {
      <div [class.g-required]="isRequired" class="upload-button">
        <a href="#" (click)="$event.preventDefault(); addFile()">{{ 'BUTTONS.ADD_FILE' | translate }}</a>
      </div>
    }
  `,
  styles: [
    `
      .upload-button {
        font-size: 1.25em;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilePickerComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class FilePickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChildren(FileInputComponent) private fileInputs: QueryList<FileInputComponent>

  @Input() uploadsPossible = true
  @Input()
  get isRequired() {
    return this._isRequired
  }
  set isRequired(value: boolean) {
    this._isRequired = coerceBooleanProperty(value)
  }

  private _isRequired: boolean

  isDisabled = false
  fileControls = new FormArray([])
  changesSubscription: Subscription

  private previousFiles = []
  private onChange: Function = () => {}

  constructor() {}

  ngOnInit() {
    this.clearValue()
  }

  writeValue(value: null) {
    this.clearValue()
  }

  registerOnChange(fn: Function) {
    this.onChange = fn
  }

  registerOnTouched(fn: Function) {}

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled
  }

  private clearValue() {
    this.unsubscribeFromChanges()
    this.fileControls.controls.forEach(() => this.fileControls.removeAt(0))
    this.fileControls.push(new FormControl())
    this.subscribeToChanges()
  }

  private emitOnChanges(files: (File | null)[]) {
    const actualFiles = files.filter(Boolean)

    const areLengthsEqual = actualFiles.length === this.previousFiles.length
    const areFilesEqual = actualFiles.every((file) => this.previousFiles.includes(file))

    if (areLengthsEqual && areFilesEqual) {
      return
    }

    this.previousFiles = actualFiles
    this.onChange(actualFiles)
  }

  private addFile() {
    const inputs = this.fileInputs.map((input) => input)
    const freeInputs = inputs.filter((input) => !input.file)
    freeInputs[0].openSelectDialog()

    if (freeInputs.length === 1) {
      this.fileControls.push(new FormControl())
    }
  }

  private subscribeToChanges() {
    this.changesSubscription = this.fileControls.valueChanges.subscribe((files: (File | null)[]) => this.emitOnChanges(files))
  }

  private unsubscribeFromChanges() {
    this.changesSubscription && this.changesSubscription.unsubscribe()
  }

  ngOnDestroy() {
    this.unsubscribeFromChanges()
  }
}
